import React, { useState } from 'react';
import { Plus, GripVertical, ExternalLink, Trash2, Edit2 } from 'lucide-react';
import { MenuItem } from '../../services/settings';

interface MenuManagementProps {
  menuItems: MenuItem[];
  onChange: (items: MenuItem[]) => void;
}

export const MenuManagement: React.FC<MenuManagementProps> = ({
  menuItems,
  onChange
}) => {
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [newItem, setNewItem] = useState<MenuItem>({
    id: '',
    label: '',
    url: '',
    target: '_blank'
  });
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = () => {
    if (!newItem.label || !newItem.url) return;

    const item: MenuItem = {
      ...newItem,
      id: Date.now().toString()
    };

    onChange([...menuItems, item]);
    setNewItem({ id: '', label: '', url: '', target: '_blank' });
    setIsAdding(false);
  };

  const handleUpdate = () => {
    if (!editingItem || !editingItem.label || !editingItem.url) return;

    const updatedItems = menuItems.map(item =>
      item.id === editingItem.id ? editingItem : item
    );

    onChange(updatedItems);
    setEditingItem(null);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce lien ?')) return;
    onChange(menuItems.filter(item => item.id !== id));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newItems = [...menuItems];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    onChange(newItems);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="w-4 h-4" />
          Ajouter un lien
        </button>
      </div>

      {isAdding && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Texte du lien
              </label>
              <input
                type="text"
                value={newItem.label}
                onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Ex: A propos"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                URL
              </label>
              <input
                type="url"
                value={newItem.url}
                onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="https://..."
              />
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newItem.target === '_blank'}
                onChange={(e) => setNewItem({ ...newItem, target: e.target.checked ? '_blank' : '_self' })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Ouvrir dans un nouvel onglet
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsAdding(false)}
              className="px-3 py-1.5 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Annuler
            </button>
            <button
              onClick={handleAdd}
              className="px-3 py-1.5 text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              Ajouter
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {menuItems.map((item, index) => (
          <div
            key={item.id}
            className={`flex items-center gap-4 p-3 rounded-lg ${
              editingItem?.id === item.id
                ? 'bg-blue-50 dark:bg-blue-900/20'
                : 'bg-gray-50 dark:bg-gray-700/50'
            }`}
          >
            {editingItem?.id === item.id ? (
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="text"
                    value={editingItem.label}
                    onChange={(e) => setEditingItem({ ...editingItem, label: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <input
                    type="url"
                    value={editingItem.url}
                    onChange={(e) => setEditingItem({ ...editingItem, url: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingItem.target === '_blank'}
                      onChange={(e) => setEditingItem({ ...editingItem, target: e.target.checked ? '_blank' : '_self' })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Ouvrir dans un nouvel onglet
                    </span>
                  </label>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {item.label}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    {item.url}
                    {item.target === '_blank' && (
                      <ExternalLink className="w-3 h-3" />
                    )}
                  </div>
                </div>
              </>
            )}
            <div className="flex items-center gap-2">
              {editingItem?.id === item.id ? (
                <>
                  <button
                    onClick={() => setEditingItem(null)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleUpdate}
                    className="p-1 text-blue-600 hover:text-blue-700"
                  >
                    Enregistrer
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setEditingItem(item)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1 text-red-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};